import { useDebounceEffect } from 'ahooks';
import { rrulestr } from 'rrule';
import type { StoredShiftEvent } from '../../hooks/types';
import { useNotify } from '../../hooks/useNotify';
import useQueryShiftEvent from '../../hooks/useQueryShiftEvent';
import { onApolloError } from '../../lib/graphql/helpers';
import { noteContentAndShiftChangesToString } from '../Notes/util';
import { ShiftEventTableRow, shiftEventTableColumns } from './lib/types';
import { searchDebounceMillis } from './lib/util';
import { useState } from 'react';
import PaginatedTable, { DEFAULTPAGESIZE } from '../PaginatedTable/index';

type ExportShiftEventTableProps = {
  readonly selectedTeamIds: readonly string[];
  readonly selectedTeamTypes: readonly string[];
  readonly selectedCostObjectIds: readonly string[];
  readonly selectedTeamRoleIds: readonly string[];
  readonly selectedTeamRoleGroups: readonly string[];
  readonly selectedUserIds: readonly string[];
  readonly selectedUserTypes: readonly string[];
  readonly fromDate: Date;
  readonly toDate: Date;
  readonly shiftEvent: readonly StoredShiftEvent[];
} & {
  readonly setShiftEvent: (value: readonly StoredShiftEvent[]) => void;
  readonly setMessage: (value: React.SetStateAction<string>) => void;
};

/**
 * Renders the table for exporting shift event.
 */
const ExportShiftEventTable: React.FC<ExportShiftEventTableProps> = ({
  fromDate,
  toDate,
  selectedCostObjectIds,
  selectedTeamIds,
  selectedTeamRoleGroups,
  selectedTeamRoleIds,
  selectedTeamTypes,
  selectedUserIds,
  selectedUserTypes,
  shiftEvent,
  setMessage,
  setShiftEvent,
}) => {
  const { notify } = useNotify();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULTPAGESIZE);

  // Shift event table rows to be displayed
  const [shiftEventTableRows, setShiftEventTableRows] = useState<
    readonly ShiftEventTableRow[]
  >([]);

  ////////////////////////////////////
  // Search and process shift event //
  ////////////////////////////////////
  const { loading: loadingShiftEvent } = useQueryShiftEvent({
    teamIds: selectedTeamIds,
    teamTypes: selectedTeamTypes,
    costObjectIds: selectedCostObjectIds,
    teamRoleIds: selectedTeamRoleIds,
    teamRoleGroups: selectedTeamRoleGroups,
    userIds: selectedUserIds,
    userTypes: selectedUserTypes,
    from: fromDate,
    to: toDate,
    onCompleted: (shiftEvent: readonly StoredShiftEvent[], msg: string) => {
      setShiftEvent(shiftEvent);
      setMessage(msg);
    },
    onError: onApolloError(notify.error),
  });

  useDebounceEffect(
    () => {
      const newShiftEventTableRows: readonly ShiftEventTableRow[] =
        shiftEvent.map((currentShiftEvent, index) => {
          const tmpShiftRecurrencePattern =
            currentShiftEvent.shiftRecurrencePattern ?? '';
          const shiftRecurrencePattern = currentShiftEvent.shiftIsRecurring
            ? rrulestr(tmpShiftRecurrencePattern).toText()
            : 'Not recurring shift';

          const shiftRecurrenceEndDate = currentShiftEvent.shiftIsRecurring
            ? new Date(currentShiftEvent.shiftRecurrenceEndDate)
            : undefined;

          const userFullName = `${currentShiftEvent.userFirstName ?? ''} ${
            currentShiftEvent.userLastName ?? ''
          }`.trim();
          const username = currentShiftEvent.username;
          const userEmail = currentShiftEvent.userEmail;
          // We show user email in bracket if email is not same as username.
          const usernameWithEmail =
            username === userEmail
              ? username
              : `${username ?? ''} (${userEmail ?? 'no email'})`;

          const shiftNoteContent = currentShiftEvent.shiftNotes;
          const shiftNote =
            shiftNoteContent !== undefined
              ? noteContentAndShiftChangesToString(
                  shiftNoteContent.metadata?.shift?.changes ?? [],
                  shiftNoteContent.content
                )
              : undefined;

          return {
            ...currentShiftEvent,
            key: `${index}`,
            eventCreatedAt: new Date(currentShiftEvent.eventCreatedAt),
            shiftStartTime: new Date(currentShiftEvent.shiftStartTime),
            shiftEndTime: new Date(currentShiftEvent.shiftEndTime),
            shiftRecurrencePattern,
            shiftRecurrenceEndDate,
            shiftNote,
            userFullName,
            username: usernameWithEmail,
          };
        });

      setShiftEventTableRows(newShiftEventTableRows);
      setCurrentPage(1);
    },
    [shiftEvent],
    { wait: searchDebounceMillis }
  );
  return (
    <PaginatedTable
      otherTableProps={{
        id: 'export-shift-event-table',
        dataSource: shiftEventTableRows,
        columns: shiftEventTableColumns,
        style: { marginTop: '10px' },
        scroll: { x: true },
        loading: loadingShiftEvent,
      }}
      pagination={{
        page: { currentPage, setCurrentPage },
        pageSize: { currentPageSize, setCurrentPageSize },
        otherPaginationProps: {
          position: ['bottomRight'],
        },
      }}
    />
  );
};

export default ExportShiftEventTable;
